import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { COUPLE_FEEDBACK_NOTE_MAX } from '@lib/constants/couple-feedback'
import { COUPLE_FEEDBACK_WORK_STATUSES } from '@lib/enums/couple-feedback-status.enum'
import type { CoupleFeedbackDto } from '@lib/dto/couple-feedback.dto'
import { updateCoupleFeedbackInputSchema } from '@lib/input/couple-feedback.input'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCoupleFeedback, useUpdateCoupleFeedback } from '@/hooks/useCoupleFeedback'
import { formatEventDate } from '@/lib/eventTime'
import { instantLabel } from '@/lib/instantLabel'
import { Button } from '@/components/ui/button'
import { Facts, type Fact } from '@/components/ui/facts'
import { Field } from '@/components/ui/field'
import { FormError } from '@/components/ui/form-error'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { guestCountLine, SENTIMENT_TITLE, STATUS_LABEL } from '@/lib/couple-feedback-labels'

function resolvedLabel(row: CoupleFeedbackDto): string | null {
  if (row.sentiment === 'wonderful') return null
  if (row.resolvedAt === null) return null
  return instantLabel(row.resolvedAt)
}

/** Each review place they opened from their page, first tap first: "Yelp, 3 Nov 2026, 2:14 pm". */
function reviewClicksLine(row: CoupleFeedbackDto): string | null {
  if (row.reviewClicks.length === 0) return null
  return row.reviewClicks.map((click) => `${click.name}, ${instantLabel(click.clickedAt)}`).join(' · ')
}

function factsFor(row: CoupleFeedbackDto): Fact[] {
  const guests = guestCountLine(row.guestCount)
  const praise = row.sentiment === 'wonderful'
  return [
    { label: 'From', value: row.authorName, empty: 'Name not set' },
    {
      label: 'Event',
      value: (
        <Link to={`/events/${row.eventId}`} className="text-accent-ink underline-offset-2 hover:underline">
          {row.eventName}
        </Link>
      ),
      empty: 'Event not set',
    },
    { label: 'Date', value: row.eventDate === null ? null : formatEventDate(row.eventDate), empty: 'Date not set' },
    { label: 'Guests', value: guests, empty: 'Guest count not set' },
    { label: 'Venue', value: row.venueName, empty: 'Venue not set' },
    { label: 'Sent', value: instantLabel(row.sentAt), empty: 'Not sent' },
    { label: 'Status', value: praise ? 'Kind words' : STATUS_LABEL[row.status], empty: 'Status not set' },
    { label: 'With', value: praise ? null : row.assigneeName, empty: praise ? 'Not a task' : 'Nobody yet' },
    { label: 'Resolved', value: resolvedLabel(row), empty: praise ? 'Not a task' : 'Not resolved' },
    { label: 'Review links opened', value: reviewClicksLine(row), empty: 'None opened' },
  ]
}

/** New, in progress, or resolved. Kind words have none of these. */
function WorkStatus({ row }: { row: CoupleFeedbackDto }) {
  const update = useUpdateCoupleFeedback(row.id)
  if (row.sentiment === 'wonderful') {
    return <p className="text-sm text-ink-muted">Kind words stay as a record. There’s nothing to resolve.</p>
  }
  return (
    <div className="space-y-2">
      <SegmentedControl
        label="Status"
        size="sm"
        value={row.status}
        onValueChange={(next) => {
          if (update.isPending || next === row.status) return
          update.mutate({ status: next })
        }}
        options={COUPLE_FEEDBACK_WORK_STATUSES.map((value) => ({ value, label: STATUS_LABEL[value] }))}
      />
      {update.isError ? <FormError>{requestErrorMessage(update.error)}</FormError> : null}
    </div>
  )
}

/** The venue's own note. The couple never sees it. */
function InternalNote({ row }: { row: CoupleFeedbackDto }) {
  const update = useUpdateCoupleFeedback(row.id)
  const [draft, setDraft] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const shown = draft ?? row.internalNote ?? ''

  function save(): void {
    if (draft === null) return
    const next = draft.trim()
    if (next === (row.internalNote ?? '')) {
      setDraft(null)
      setError(null)
      return
    }
    const parsed = updateCoupleFeedbackInputSchema.safeParse({ internalNote: next.length === 0 ? null : next })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'That isn’t right')
      return
    }
    setError(null)
    update.mutate(parsed.data, {
      onSuccess: () => setDraft(null),
      onError: (cause) => setError(requestErrorMessage(cause)),
    })
  }

  return (
    <Field
      label="Note for the venue"
      htmlFor="feedback-note"
      hint={`Only your staff see this. ${String(shown.length)} of ${COUPLE_FEEDBACK_NOTE_MAX.toLocaleString('en-US')} characters.`}
      error={error}
    >
      <Textarea
        id="feedback-note"
        rows={4}
        maxLength={COUPLE_FEEDBACK_NOTE_MAX}
        value={shown}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
      />
    </Field>
  )
}

/**
 * One answer, and the work on it when something needs attention. The note
 * stays inside the venue.
 */
export function FeedbackDetailPage() {
  const { feedbackId } = useParams()
  const id = feedbackId ?? ''
  const query = useCoupleFeedback(id)

  if (query.isPending) {
    return (
      <PageContainer>
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (query.isError) {
    return (
      <PageContainer className="space-y-4">
        <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
        <Button variant="ghost" size="sm" asChild>
          <Link to="/feedback">Back to feedback</Link>
        </Button>
      </PageContainer>
    )
  }

  const row = query.data
  const note = row.body !== null && row.body.length > 0
  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title={SENTIMENT_TITLE[row.sentiment]}
        meta={`${row.authorName} · ${row.eventName}`}
        description="Private to the venue. Opening a review link is a tap on our button, not a published review."
      />
      <Button variant="ghost" size="sm" asChild>
        <Link to="/feedback">Back to feedback</Link>
      </Button>
      {note ? <p className="m-0 min-w-0 wrap-anywhere whitespace-pre-wrap text-sm">{row.body}</p> : <p className="m-0 text-sm text-ink-muted">No note.</p>}
      <Facts facts={factsFor(row)} layout="stack" />
      <WorkStatus row={row} />
      <InternalNote row={row} />
    </PageContainer>
  )
}
